import { isInIframe } from "./url-utils";
import { detectIncognito } from "detectincognitojs";

export enum PlatformType {
  Windows,
  Macintosh,
  iPhone,
  iPad,
  iPod,
  Android,
  Linux,
  Unknown,
}

let platformType: PlatformType;

export function getPlatformType() {
  if (platformType == undefined) {
    if ("userAgentData" in (navigator as any)) {
      const platform = (navigator as any).userAgentData.platform;
      if (platform == "macOS") platformType = PlatformType.Macintosh;
      else if (platform == "Windows") platformType = PlatformType.Windows;
      else if (platform == "Linux") platformType = PlatformType.Linux;
      else platformType = PlatformType.Unknown;
    } else {
      const userAgent = navigator.userAgent;
      if (userAgent.includes("Windows")) platformType = PlatformType.Windows;
      else if (userAgent.includes("Macintosh")) platformType = PlatformType.Macintosh;
      else if (userAgent.includes("iPhone")) platformType = PlatformType.iPhone;
      else if (userAgent.includes("iPad")) platformType = PlatformType.iPad;
      else if (userAgent.includes("iPod")) platformType = PlatformType.iPod;
      else if (userAgent.includes("Android"))
        // must test before linux, since android includes linux
        platformType = PlatformType.Android;
      else if (userAgent.includes("Linux")) platformType = PlatformType.Linux;
      else platformType = PlatformType.Unknown;
    }
  }
  return platformType;
}

export function getBrowser() {
  const userAgent = navigator.userAgent;
  let browserName;

  if (userAgent.match(/chrome|chromium|crios/i)) {
    browserName = "chrome";
  } else if (userAgent.match(/firefox|fxios/i)) {
    browserName = "firefox";
  } else if (userAgent.match(/safari/i)) {
    browserName = "safari";
  } else if (userAgent.match(/opr\//i)) {
    browserName = "opera";
  } else if (userAgent.match(/edg/i)) {
    browserName = "edge";
  } else {
    browserName = "";
  }
  return browserName;
}

export function getQueryParam(name: string) {
  try {
    const url = window.location.href;
    const params = new URL(url).searchParams;
    return params.get(name);
  } catch {
    return null;
  }
}

export async function readLocalStorage(key: string): Promise<string | null> {
  // the easy case - we're not in iframe we have access
  if (!isInIframe()) {
    return window.localStorage.getItem(key);
  }
  // if we're in iframe the browser isolates our local-storage
  // check for the new APIs that give us access
  if (document.hasStorageAccess) {
    try {
      const hasAccess = await document.hasStorageAccess();
      if (hasAccess) {
        //@ts-expect-error we really need typescript 5
        const handle: any = (await document.requestStorageAccess({ localStorage: true })) as any;
        return handle ? handle.localStorage.getItem(key) : null;
      } else {
        //@ts-expect-error we really need typescript 5
        const permission = await navigator.permissions.query({ name: "storage-access" });
        if (permission.state === "granted") {
          // If so, you can just call requestStorageAccess() without a user interaction,
          // and it will resolve automatically.
          //@ts-expect-error we really need typescript 5
          const handle: any = await document.requestStorageAccess({ localStorage: true });
          return handle ? handle.localStorage.getItem(key) : null;
        } else if (permission.state === "denied") {
          // can't do much - user/browser won't let us access local-storage
        } else if (permission.state === "prompt") {
          //@ts-expect-error we really need typescript 5
          const handle: any = await document.requestStorageAccess({ localStorage: true });
          return handle ? handle.localStorage.getItem(key) : null;
        }
      }
    } catch {
      console.log("Failed getting access to local clipboard, keep your fingers crossed");
      return window.localStorage.getItem(key);
    }
  }
  return null;
}

/**
 * Detects if the browser is in incognito/private browsing mode.
 * Uses detectincognitojs library for cross-browser detection.
 */
export async function isIncognitoMode(): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const result = await detectIncognito();
    return result.isPrivate;
  } catch {
    return false;
  }
}

export async function writeLocalStorage(key: string, value: string) {
  if (!window) {
    return;
  }
  // the easy case - we're not in iframe we have access
  if (!isInIframe()) {
    return window.localStorage.setItem(key, value);
  }

  if (document.hasStorageAccess) {
    try {
      const hasAccess = await document.hasStorageAccess();
      if (hasAccess) {
        //@ts-expect-error we really need typescript 5
        const handle: any = (await document.requestStorageAccess({ localStorage: true })) as any;
        handle && handle.localStorage.setItem(key, value);
      } else {
        //@ts-expect-error we really need typescript 5
        const permission = await navigator.permissions.query({ name: "storage-access" });
        if (permission.state === "granted") {
          // If so, you can just call requestStorageAccess() without a user interaction,
          // and it will resolve automatically.
          //@ts-expect-error we really need typescript 5
          const handle: any = await document.requestStorageAccess({ localStorage: true });
          handle && handle.localStorage.setItem(key, value);
        } else if (permission.state === "denied") {
          // can't do much - user/browser won't let us access local-storage
        } else if (permission.state === "prompt") {
          //@ts-expect-error we really need typescript 5
          const handle: any = await document.requestStorageAccess({ localStorage: true });
          handle && handle.localStorage.setItem(key, value);
        }
      }
    } catch {
      console.log("Failed getting access to local clipboard, keep your fingers crossed");
      window.localStorage.setItem(key, value);
    }
  }
}
